import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom'

import './index.css'
import { AppShell } from '@/components/AppShell'
import { AuthProvider, useAuth } from '@/lib/auth'
import { hasAnyPerm, isPrivileged } from '@/lib/perms'
import { PERM, type Permission } from '@/types/api'
import { AppReviewPage } from '@/pages/AppReview'
import { AuditLogPage } from '@/pages/AuditLog'
import { CatalogPage } from '@/pages/Catalog'
import { CronPage } from '@/pages/Cron'
import { CronDetailPage } from '@/pages/CronDetail'
import { DashboardPage } from '@/pages/Dashboard'
import { FalModelDetailPage } from '@/pages/FalModelDetail'
import { FalModelsPage } from '@/pages/FalModels'
import { GenerationsPage } from '@/pages/Generations'
import { GenerationDetailPage } from '@/pages/GenerationDetail'
import { LoginPage } from '@/pages/Login'
import { LoginCarouselPage } from '@/pages/LoginCarousel'
import { MonetizationPage } from '@/pages/Monetization'
import { PaywallCarouselPage } from '@/pages/PaywallCarousel'
import { PhotoshootsPage } from '@/pages/Photoshoots'
import { ProfilePage } from '@/pages/Profile'
import { RolesPage } from '@/pages/Roles'
import { SettingsPage } from '@/pages/Settings'
import { SupportPage } from '@/pages/Support'
import { SupportTicketDetailPage } from '@/pages/SupportTicketDetail'
import { UserDetailPage } from '@/pages/UserDetail'
import { UsersPage } from '@/pages/Users'

// Single client per process. `staleTime: 30s` keeps the typical admin
// page from refetching on every focus toggle; `retry: 1` mirrors the
// iOS APIClient retry budget for transient blips.
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } },
})

/**
 * Permission gate (PX-73). Allows the route through when the profile
 * carries ANY of the requested permissions (or the admin wildcard).
 *
 *   - No profile → redirect to /login.
 *   - Plain user (no permissions) → redirect to /login (they can't
 *     reach any admin surface).
 *   - Profile exists but lacks the required permission → redirect to
 *     the first surface they CAN reach (Photoshoots is the canonical
 *     landing for non-admin privileged roles).
 */
function RequirePermission({ perms }: { perms: Permission[] }) {
  const { ready, profile } = useAuth()
  if (!ready) {
    return (
      <div className="flex h-full w-full items-center justify-center text-sm text-ink-400">
        Loading…
      </div>
    )
  }
  if (!profile) return <Navigate to="/login" replace />
  if (!isPrivileged(profile)) return <Navigate to="/login" replace />
  if (!hasAnyPerm(profile, perms)) return <Navigate to="/photoshoots" replace />
  return <Outlet />
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            {/* Curator-eligible surfaces */}
            <Route element={<RequirePermission perms={[PERM.photoshoots_read]} />}>
              <Route element={<AppShell />}>
                <Route path="photoshoots" element={<PhotoshootsPage />} />
              </Route>
            </Route>
            <Route element={<RequirePermission perms={[PERM.models_read, PERM.plans_read]} />}>
              <Route element={<AppShell />}>
                <Route path="catalog" element={<CatalogPage />} />
              </Route>
            </Route>
            {/* Monetization (subscription products / credit packs / signup
                bonuses) reuses plans.read / plans.write server-side. */}
            <Route element={<RequirePermission perms={[PERM.plans_read]} />}>
              <Route element={<AppShell />}>
                <Route path="monetization" element={<MonetizationPage />} />
              </Route>
            </Route>
            {/* Custom media — login-screen carousel (PX-252). */}
            <Route element={<RequirePermission perms={[PERM.login_images_read]} />}>
              <Route element={<AppShell />}>
                <Route path="login-carousel" element={<LoginCarouselPage />} />
              </Route>
            </Route>
            {/* Custom media — paywall carousel (PX-254). */}
            <Route element={<RequirePermission perms={[PERM.paywall_images_read]} />}>
              <Route element={<AppShell />}>
                <Route path="paywall-carousel" element={<PaywallCarouselPage />} />
              </Route>
            </Route>
            <Route element={<RequirePermission perms={[PERM.photoshoots_read]} />}>
              <Route element={<AppShell />}>
                <Route path="featured" element={<Navigate to="/photoshoots" replace />} />
              </Route>
            </Route>

            {/* Any-privileged-role surfaces (no perm needed) */}
            <Route element={<RequirePermission perms={[
              PERM.photoshoots_read,
              PERM.models_read,
              PERM.plans_read,
              PERM.users_read,
              PERM.roles_read,
              PERM.audit_read,
              PERM.generations_read,
              PERM.settings_write,
            ]} />}>
              <Route element={<AppShell />}>
                <Route path="profile" element={<ProfilePage />} />
              </Route>
            </Route>

            {/* Admin-side surfaces — gated per-page so a custom
                "audit-only" role could access just the audit log. */}
            <Route element={<RequirePermission perms={[PERM.users_read]} />}>
              <Route element={<AppShell />}>
                <Route index element={<DashboardPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="users/:userId" element={<UserDetailPage />} />
              </Route>
            </Route>
            <Route element={<RequirePermission perms={[PERM.roles_read]} />}>
              <Route element={<AppShell />}>
                <Route path="roles" element={<RolesPage />} />
              </Route>
            </Route>
            <Route element={<RequirePermission perms={[PERM.generations_read]} />}>
              <Route element={<AppShell />}>
                <Route path="generations" element={<GenerationsPage />} />
                <Route path="generations/:id" element={<GenerationDetailPage />} />
              </Route>
            </Route>
            <Route element={<RequirePermission perms={[PERM.support_read]} />}>
              <Route element={<AppShell />}>
                <Route path="support" element={<SupportPage />} />
                <Route path="support/:ticketId" element={<SupportTicketDetailPage />} />
              </Route>
            </Route>
            <Route element={<RequirePermission perms={[PERM.audit_read]} />}>
              <Route element={<AppShell />}>
                <Route path="audit-log" element={<AuditLogPage />} />
              </Route>
            </Route>
            <Route element={<RequirePermission perms={[PERM.settings_write]} />}>
              <Route element={<AppShell />}>
                <Route path="settings" element={<SettingsPage />} />
              </Route>
            </Route>
            <Route element={<RequirePermission perms={[PERM.fal_models_read]} />}>
              <Route element={<AppShell />}>
                <Route path="fal-models" element={<FalModelsPage />} />
                {/* Splat — endpoint ids carry slashes
                    (e.g. fal-ai/nano-banana-2/edit). */}
                <Route path="fal-models/*" element={<FalModelDetailPage />} />
              </Route>
            </Route>
            <Route element={<RequirePermission perms={[PERM.cron_read]} />}>
              <Route element={<AppShell />}>
                <Route path="cron" element={<CronPage />} />
                <Route path="cron/:name" element={<CronDetailPage />} />
              </Route>
            </Route>
            <Route element={<RequirePermission perms={[PERM.app_review_read]} />}>
              <Route element={<AppShell />}>
                <Route path="app-review" element={<AppReviewPage />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
